/**
 * UI-only mock campaigns for local frontend work without a backend.
 *
 * Enable in development only (production builds never use this file’s data):
 * - `.env.local`: REACT_APP_USE_MARKETING_MOCK_DATA=true (unified campaigns + templates), or
 *   REACT_APP_MOCK_MARKETING_CAMPAIGNS=true → restart `npm start`
 * - Or console: localStorage.setItem('mockMarketingCampaigns','1'); location.reload()
 * - With a real API in dev: `REACT_APP_FORCE_LIVE_MARKETING_API=true` disables mocks.
 *
 * Logic: `fetchAllCampaigns` in `src/store/actions/marketing.action.ts`.
 */
import { StatusTypes } from '@components/common/StatusBadge/StatusBadge.component'
import {
  CampaignMessageStatus,
  CampaignType,
  ICampaignMessage,
  ICampaignResponse,
} from '@dataTypes/marketing.type'
import { getEnv } from '@utils/env'

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`
}

/** yyyy-MM-dd */
function ymd(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function atHours(d: Date, h: number, min: number): string {
  const x = new Date(d)
  x.setHours(h, min, 0, 0)
  return x.toISOString()
}

function buildMessages(
  campaignId: string,
  specs: { sent: boolean; failed?: boolean }[],
): ICampaignMessage[] {
  const now = new Date()
  return specs.map((s, i) => {
    const day = new Date(now)
    day.setDate(day.getDate() - i)
    return {
      contact_id: `mock-contact-${campaignId}-${i}`,
      campaign_id: campaignId,
      classification: 'Main',
      message_type: 'Outgoing',
      content: s.failed ? 'Undeliverable' : 'Mock message',
      cellphone: '5550100',
      status: s.failed
        ? CampaignMessageStatus.failed
        : CampaignMessageStatus.sent,
      error_code: s.failed ? '30007' : undefined,
      resend_count: 0,
      date_time_sent: atHours(day, 10 + (i % 6), 0),
    }
  })
}

export function getMockMarketingCampaigns(): ICampaignResponse[] {
  if (getEnv('NODE_ENV') === 'production') {
    console.error(
      '[Marketing] getMockMarketingCampaigns must not run in production builds; returning [].',
    )
    return []
  }
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  const d = now.getDate()

  const monthStart = new Date(y, m, 1)
  const monthMid = new Date(y, m, Math.min(15, d))
  const monthEnd = new Date(y, m, Math.min(28, new Date(y, m + 1, 0).getDate()))
  const weekAgo = new Date(y, m, Math.max(1, d - 5))
  const weekAhead = new Date(
    y,
    m,
    Math.min(new Date(y, m + 1, 0).getDate(), d + 7),
  )

  const base = (
    id: string,
    partial: Partial<ICampaignResponse> &
      Pick<ICampaignResponse, 'name' | 'channel'>,
  ): ICampaignResponse => ({
    campaign_id: id,
    name: partial.name,
    description: partial.description ?? 'Mock campaign for local UI preview.',
    type: partial.type ?? CampaignType.Promotion,
    channel: partial.channel,
    message_status: partial.message_status ?? 'Sent',
    campaign_status: partial.campaign_status ?? 'Active',
    timezone: 'America/New_York',
    start_date: partial.start_date ?? ymd(monthStart),
    end_date: partial.end_date ?? ymd(monthEnd),
    start_time: partial.start_time ?? '08:00',
    end_time: partial.end_time ?? '17:00',
    location_filter: [],
    patient_status_filter: [],
    age_range_filter: [],
    appt_status_filter: [],
    recall_type_filter: [],
    celebration_type_filter: [],
    created_by: 'mock-user',
    updated_by: 'mock-user',
    campaign_recipient: [],
    campaign_trigger_keyword: [],
    campaign_trigger_message: [],
    campaign_message: partial.campaign_message ?? [],
    platform: 'mock',
    sent: partial.sent ?? 42,
    open: partial.open ?? 10,
    clicks: partial.clicks ?? 3,
    statusType: partial.statusType ?? StatusTypes.Active,
    uploadTime: '12:00 PM',
    uploadDate: ymd(now),
  })

  const c1 = 'mock-campaign-1'
  const c2 = 'mock-campaign-2'
  const c3 = 'mock-campaign-3'
  const c4 = 'mock-campaign-4'
  const c5 = 'mock-campaign-5'
  const c6 = 'mock-campaign-6'

  return [
    base(c1, {
      name: 'Spring recall & recare',
      type: CampaignType.Recall,
      channel: 'SMS',
      message_status: 'Sent',
      campaign_status: 'Active',
      start_date: ymd(weekAgo),
      end_date: ymd(weekAhead),
      campaign_message: buildMessages(c1, [
        { sent: true },
        { sent: true },
        { sent: true },
        { sent: false, failed: true },
      ]),
    }),
    base(c2, {
      name: 'Birthday celebrations',
      type: CampaignType.Celebration,
      channel: 'Email',
      message_status: 'Scheduled',
      campaign_status: 'Inactive',
      start_date: ymd(monthMid),
      end_date: ymd(monthEnd),
      campaign_message: buildMessages(c2, [{ sent: true }, { sent: true }]),
    }),
    base(c3, {
      name: 'Post-visit follow-up',
      type: CampaignType['Follow-up'],
      channel: 'SMS',
      message_status: 'Draft',
      campaign_status: 'Inactive',
      start_date: ymd(monthStart),
      end_date: ymd(monthMid),
      start_time: '09:30',
      end_time: '16:00',
      campaign_message: [],
    }),
    base(c4, {
      name: 'Patient education — fluoride',
      type: CampaignType['Pat. edu'],
      channel: 'Email',
      message_status: 'Sent',
      campaign_status: 'Active',
      start_date: ymd(new Date(y, m, Math.max(1, d - 2))),
      end_date: ymd(new Date(y, m, Math.min(28, d + 3))),
      campaign_message: buildMessages(c4, [
        { sent: true },
        { sent: true },
        { sent: true },
      ]),
    }),
    base(c5, {
      name: 'Promotional whitening week',
      type: CampaignType.Promotion,
      channel: 'SMS',
      message_status: 'Sent',
      campaign_status: 'Active',
      start_date: ymd(now),
      end_date: ymd(weekAhead),
      campaign_message: buildMessages(c5, [{ sent: true }, { sent: true }]),
    }),
    base(c6, {
      name: 'Appointment reminders',
      type: CampaignType.Reminder,
      channel: 'SMS',
      message_status: 'Scheduled',
      campaign_status: 'Active',
      start_date: ymd(monthStart),
      end_date: ymd(monthEnd),
      campaign_message: buildMessages(c6, [{ sent: true }]),
    }),
  ]
}
